<template>
  <div class="question" v-on:focusout="hideCopyButton">
    <div class="row">
      <div class="col-12 question__question">
        <span v-bind:inner-html.prop="answer.question | marked | noParagraph"></span>
        <span class="question__required" v-if="answer.required">*</span>
      </div>
    </div>

    <div v-if="answer.note" class="question__note">
      <div v-bind:inner-html.prop="answer.note | marked" class="question__note-inner"></div>
    </div>

    <div class="row" v-if="editable">
      <div class="col-11">
        <ValidationProvider v-bind:name="answer.reference" v-slot="{ errors }" v-bind:rules="rules" ref="answerValidator">

          <div v-if="isTime">
            <flat-pickr v-model="answer.answer" v-bind:config="flatPickrConfigTime" v-on:input="updateAnswer"></flat-pickr>
          </div>

          <div v-if="isDate" class="question__answer--input-with-button">
            <flat-pickr v-model="answer.answer"
                        ref="dateComponent"
                        v-bind:config="flatPickrConfigDate"
                        v-on:input="updateAnswer"></flat-pickr>
            <a title="Select date" v-on:click.stop="openDateComponent">
              <i class="fas fa-calendar-alt"></i>
            </a>
          </div>

          <div v-if="isDateOfBirth" class="question__answer--input-with-button">
            <flat-pickr v-model="answer.answer"
                        ref="dateOfBirthComponent"
                        v-bind:config="flatPickrConfigDate"
                        v-on:input="updateAnswer"></flat-pickr>
            <a title="Select date" v-on:click.stop="openDateOfBirthComponent">
              <i class="fas fa-calendar-alt"></i>
            </a>
          </div>

          <div v-if="isIDNumber" class="question__answer--copyable-input">
            <input type="text" v-model="answer.answer" class="short-question"
                   v-on:change="updateAnswer"
                   v-on:keypress="clearServerValidationState"
                   v-on:keydown.space.prevent/>
          </div>

          <div v-if="isEmail" class="question__answer--copyable-input">
            <input type="email" v-model="answer.answer" class="short-question"
                   v-on:change="updateAnswer"
                   v-on:focusin="checkShowCopyButton"
                   v-on:keypress="clearServerValidationState"
                   v-on:keydown.tab.exact="checkIfTabbingToCopyButton"
                   v-on:keydown.shift.tab="hideCopyButton"/>
            <span v-if="answer.copyable">
              <ul v-show="isShowTextToCopy"
                  v-on:keydown.up.prevent="keyPressed"
                  v-on:keydown.down.prevent="keyPressed">
                <li v-for="cachedAnswer in getCachedAnswers()"
                    v-bind:key="cachedAnswer.reference"
                    v-on:mousedown="pasteContent(cachedAnswer)"
                    v-on:keydown.enter="pasteContent(cachedAnswer)"
                    v-on:keydown.space="pasteContent(cachedAnswer)"
                    tabindex="0"
                    v-on:keydown.up.prevent="keyPressed"
                    v-on:keydown.down.prevent="keyPressed">{{ cachedAnswer }}</li>
              </ul>
              <button v-show="isShowCopyButton"
                      class="btn btn-secondary"
                      v-on:mousedown.prevent="showTextToCopy()"
                      v-on:keydown.enter.prevent="showTextToCopy()"
                      v-on:keydown.space.prevent="showTextToCopy()"
                      v-on:focusout="buttonLost">
                <i class="fa fa-copy"></i>
              </button>
            </span>
          </div>

          <div v-if="isShortAnswer || isFirstName || isLastName" class="question__answer--copyable-input">
            <input type="text" v-model="answer.answer" class="short-question"
                   v-on:change="updateAnswer"
                   v-on:focusin="checkShowCopyButton"
                   v-on:keypress="clearServerValidationState"
                   v-on:keydown.tab.exact="checkIfTabbingToCopyButton"
                   v-on:keydown.shift.tab="hideCopyButton"/>
            <span v-if="answer.copyable">
              <ul v-show="isShowTextToCopy"
                  v-on:keydown.up.prevent="keyPressed"
                  v-on:keydown.down.prevent="keyPressed">
                <li v-for="cachedAnswer in getCachedAnswers()"
                     v-bind:key="cachedAnswer.reference"
                     v-on:mousedown="pasteContent(cachedAnswer)"
                     v-on:keydown.enter="pasteContent(cachedAnswer)"
                     v-on:keydown.space="pasteContent(cachedAnswer)"
                     tabindex="0"
                     v-on:keydown.up.prevent="keyPressed"
                     v-on:keydown.down.prevent="keyPressed">{{ cachedAnswer }}</li>
              </ul>
              <button v-show="isShowCopyButton" class="btn btn-secondary"
                      v-on:mousedown.prevent="showTextToCopy()"
                      v-on:keydown.enter.prevent="showTextToCopy()"
                      v-on:keydown.space.prevent="showTextToCopy()"
                      v-on:focusout="buttonLost">
                <i class="fa fa-copy"></i>
              </button>
            </span>
          </div>

          <div v-if="isMobile">
            <vue-tel-input v-model="answer.answer"
                           v-bind:mode="'international'"
                           v-bind:enabledCountryCode="true"
                           v-bind:preferredCountries="['ZA']"
                           v-bind:autoFormat="false"
                           v-bind:defaultCountry="'ZA'"
                           v-on:blur="updateAnswer"
                           v-on:input="onTelephoneNumberChanged"
                           v-on:open="onTelephoneDropdownToggle(true)"
                           v-on:close="onTelephoneDropdownToggle(false)">
              <template v-slot:arrow-icon>
                <span>{{ isTelephoneDropdownOpen ? '▲' : '▼' }}</span>
              </template>
            </vue-tel-input>
          </div>

          <div v-if="isParagraph" class="question__answer--copyable-input">
            <textarea v-model="answer.answer" class="long-question" ref="resizeableTextarea"
                      :style="textAreaStyle"
                      v-on:change="updateAnswer"
                      v-on:focusin="checkShowCopyButton"
                      v-on:keypress="clearServerValidationState"
                      v-on:keydown.tab.exact="checkIfTabbingToCopyButton"
                      v-on:keydown.shift.tab="hideCopyButton"
                      v-on:input="resizeTextArea"/>
            <span v-if="answer.copyable">
              <ul v-show="isShowTextToCopy"
                  v-on:keydown.up.prevent="keyPressed"
                  v-on:keydown.down.prevent="keyPressed">
                <li v-for="cachedAnswer in getCachedAnswers()"
                    v-bind:key="cachedAnswer.reference"
                    v-on:mousedown="pasteContent(cachedAnswer)"
                    v-on:keydown.enter="pasteContent(cachedAnswer)"
                    v-on:keydown.space="pasteContent(cachedAnswer)"
                    tabindex="0"
                    v-on:keydown.up.prevent="keyPressed"
                    v-on:keydown.down.prevent="keyPressed">{{ cachedAnswer }}</li>
              </ul>
              <button v-show="isShowCopyButton" class="btn btn-secondary"
                      v-on:mousedown.prevent="showTextToCopy()"
                      v-on:keydown.enter.prevent="showTextToCopy()"
                      v-on:keydown.space.prevent="showTextToCopy()"
                      v-on:focusout="buttonLost">
                <i class="fa fa-copy"></i>
              </button>
            </span>
          </div>

          <div v-if="isMultipleChoice" class="answer-group" v-bind:class="{ 'answer-group--horizontal': answer.displayOptionsHorizontally }">
            <label v-for="option in answer.options" v-bind:key="option">
              <input type="radio" v-model="answer.answer" v-on:change="updateAnswer" v-bind:value="option" />{{ option }}
            </label>
          </div>

          <div v-if="isCheckBoxes" class="answer-group" v-bind:class="{ 'answer-group--horizontal': answer.displayOptionsHorizontally }">
            <label v-for="option in answer.options" v-bind:key="option">
              <input type="checkbox" v-model="answer.answers" v-on:change="updateAnswer" v-bind:value="option" />{{ option }}
            </label>
          </div>

          <div v-if="isDropDown" class="answer-group">
            <select v-model="answer.answer" v-on:change="updateAnswer">
              <option value="">Select</option>
              <option v-for="option in answer.options" v-bind:key="option" v-bind:value="option">{{ option }}</option>
            </select>
          </div>

          <div v-if="isCountry" class="answer-group">
            <select v-model="answer.answer" v-on:change="updateAnswer">
              <option>Select</option>
              <option v-bind:value="getSouthAfrica().code">{{ getSouthAfrica().name }}</option>
              <option disabled>──────────</option>
              <option v-for="country in countries" v-bind:key="country.code" v-bind:value="country.code">{{ country.name }}</option>
            </select>
          </div>

          <div v-if="isImage" class="answer-group">
            <input type="file" v-on:change="onFileChanged" accept="image/*" class="image-question" />
            <input type="hidden" v-model="answer.answer">
          </div>

          <div v-if="isSignature" class="signature-container">
            <div v-show="answer.answer" class="signature__preview">
              <img :src="answer.answer" alt="Image Preview">
            </div>
            <div v-show="!answer.answer">
              <canvas ref="canvas" class="signature-canvas" @mousedown="hideSignaturePlaceholder" @touchstart="hideSignaturePlaceholder"></canvas>
              <img v-show="showSignaturePlaceholder" src="@/assets/signature_placeholder.png" alt="Sign here placeholder" class="signature-placeholder"/>
            </div>
            <div class="signature-container__buttons">
              <button @click.prevent="clearSignature" class="btn btn-secondary mr-2">Clear</button>
              <button v-if="!answer.answer" @click.prevent="saveSignature" class="btn btn-primary">Save</button>
            </div>
            <input type="hidden" v-model="answer.answer">
          </div>

          <span class="pb-4 valid-error" v-if="errors">{{ errors[0] }}</span>
          <span class="pb-4 valid-error" v-if="!isShowUpdatingServer && answerFailed">{{ serverErrorMessage }}</span>

        </ValidationProvider>
      </div>
      <div class="question__activity">
        <Spinner v-show="isShowUpdatingServer" class="question__activity-spinner" />
        <i v-show="!isShowUpdatingServer && answerAccepted" class="fas fa-check text-success"></i>
        <span v-show="!isShowUpdatingServer && answerFailed" class="question__input-fail">X</span>
      </div>
    </div>

    <div class="row" v-else>
      <div class="col-12 question__answer">
        <span v-if="noAnswer">-- empty --</span>
        <span v-else-if="isCountry">{{ getCountryNameFromCode(answer.answer) }}</span>
        <span v-else-if="isMultipleAnswers">{{ getMultipleAnswers }}</span>
        <span v-else>{{ answer.answer }}</span>
      </div>
    </div>

    <div v-if="isImage && (imagePreview || answer.answer)" class="row">
      <div class="col-12">
        <div class="question__answer__preview-image">
          <img v-if="imagePreview" v-bind:src="imagePreview" alt="Image Preview" />
          <img v-else v-bind:src="answer.answer" alt="Image Preview" />
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import Spinner from '../components/Spinner.vue'
import { ValidationProvider } from 'vee-validate'
import { countriesMixin } from '../mixins/CountriesMixin'

import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import 'flatpickr/dist/themes/material_blue.css'
import SignaturePad from 'signature_pad'

const VueTelInput = () =>
  Promise.all([
    import(/* webpackChunkName: "chunk-vue-tel-input" */ 'vue-tel-input'),
    import(/* webpackChunkName: "chunk-vue-tel-input" */ 'vue-tel-input/dist/vue-tel-input.css')
  ]).then(([{ VueTelInput }]) => VueTelInput)

export default {
  name: 'FormAnswer',
  components: { Spinner, FlatPickr, ValidationProvider, VueTelInput },
  props: {
    answer: Object,
    guestReference: String,
    responseReference: String,
    answerCache: Object,
    editable: Boolean,
    sectionVisible: Boolean
  },
  data: function () {
    return {
      image: null,
      imagePreview: null,
      signaturePad: null,
      showSignaturePlaceholder: true,
      flatPickrConfigTime: {
        noCalendar: true,
        enableTime: true,
        time_24hr: true,
        dateFormat: 'H:i'
      },
      flatPickrConfigDate: {
        altInput: true,
        altFormat: 'F j Y',
        dateFormat: 'Y-m-d',
        maxDate: 'today'
      },
      isShowCopyButton: false,
      isShowTextToCopy: false,
      isFocusOnCopyControls: false,
      isShowUpdatingServer: false,
      isTelephoneDropdownOpen: false,
      answerAccepted: false,
      answerFailed: false,
      serverErrorMessage: '',
      textAreaHeight: '0'
    }
  },
  filters: {
    noParagraph: function (value) {
      if (!value) {
        return ''
      }
      value = value.toString()
      return value.replace(/<p>/i, '').replace(/<\/p>/i, '')
    }
  },
  mixins: [countriesMixin],
  computed: {
    name: function () {
      return this.answer.reference
    },
    rules: function () {
      let rules = ''
      if (this.answer.required) {
        rules += 'required'
      }
      if (this.answer.required && (this.isShortAnswer || this.isParagraph)) {
        rules += '|min:2|max:255'
      }
      if (this.answer.required && (this.isEmail)) {
        rules += '|email|max:255'
      }
      if (this.answer.required && (this.isIDNumber)) {
        rules += '|min:6|max:13'
      }
      return rules
    },
    isTime: function () {
      return String(this.answer.type) === 'time'
    },
    isFirstName: function () {
      return String(this.answer.type) === 'first-name'
    },
    isLastName: function () {
      return String(this.answer.type) === 'last-name'
    },
    isEmail: function () {
      return String(this.answer.type) === 'email'
    },
    isMobile: function () {
      return String(this.answer.type) === 'mobile'
    },
    isIDNumber: function () {
      return String(this.answer.type) === 'id-number'
    },
    isShortAnswer: function () {
      return String(this.answer.type) === 'short-answer'
    },
    isMultipleChoice: function () {
      return String(this.answer.type) === 'multiple-choice'
    },
    isCheckBoxes: function () {
      return String(this.answer.type) === 'checkboxes'
    },
    isDropDown: function () {
      return String(this.answer.type) === 'dropdown'
    },
    isParagraph: function () {
      return String(this.answer.type) === 'paragraph'
    },
    isCountry: function () {
      return String(this.answer.type) === 'country'
    },
    isDate: function () {
      return String(this.answer.type) === 'date'
    },
    isDateOfBirth: function () {
      return String(this.answer.type) === 'date-of-birth'
    },
    isImage: function () {
      return String(this.answer.type) === 'image'
    },
    isSignature: function () {
      return String(this.answer.type) === 'signature'
    },
    isMultipleAnswers: function () {
      return this.answer.answers.length > 0
    },
    isMultipleAnswersAndIsAnswered: function () {
      return this.isMultipleAnswers && this.answer.answers.length > 0
    },
    getMultipleAnswers: function () {
      let reply = ''
      for (const answer of this.answer.answers) {
        if (reply.length > 0) {
          reply += ', '
        }
        reply += answer
      }
      return reply
    },
    checkBoxVal: {
      get: function () {
        return Boolean(this.answer.answer)
      },
      set: function (value) {
        this.answer.answer = value.toString()
      }
    },
    textAreaStyle: function () {
      return {
        'min-height': this.textAreaHeight
      }
    },
    noAnswer: function () {
      return ((!this.answer.answer || this.answer.answer === '') && !this.isMultipleAnswersAndIsAnswered)
    }
  },
  methods: {
    openDateComponent: function () {
      this.$refs.dateComponent.fp.open()
    },
    openDateOfBirthComponent: function () {
      this.$refs.dateOfBirthComponent.fp.open()
    },
    onTelephoneDropdownToggle: function (open) {
      this.isTelephoneDropdownOpen = open
    },
    onTelephoneNumberChanged: function (number, phoneObject) {
      this.answer.answer = phoneObject.number
    },
    onFileChanged: function (event) {
      const file = event.target.files[0] // Get the selected file
      if (file && (file.type.startsWith('image/') || file.type.startsWith('application/pdf'))) {
        this.image = file
        this.createImagePreview(file)
        this.updateFileAnswerOnServer()
      } else {
        this.resetImage()
        alert('Please upload a valid image file (JPEG, GIF, PNG, PDF etc).')
      }
    },
    createImagePreview: function (file) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (e) => {
        this.imagePreview = e.target.result
      }
    },
    resetImage: function () {
      this.image = null
      this.imagePreview = null
    },
    keyPressed: function () {
    },
    resizeTextArea: function () {
      if (this.$refs.resizeableTextarea) {
        this.textAreaHeight = this.$refs.resizeableTextarea.scrollHeight + 'px'
      }
    },
    checkShowCopyButton: function () {
      if (this.answer.copyable && this.hasCachedAnswers()) {
        this.isShowCopyButton = true
      }
    },
    hideCopyButton: function () {
      if (!this.isFocusOnCopyControls) {
        this.isShowCopyButton = false
        this.isShowTextToCopy = false
      }
    },
    checkIfTabbingToCopyButton: function () {
      if (this.isShowCopyButton) {
        this.isFocusOnCopyControls = true
      } else {
        this.isFocusOnCopyControls = false
      }
    },
    buttonLost: function () {
      this.isShowTextToCopy = false
      this.isFocusOnCopyControls = false
    },
    showTextToCopy: function () {
      this.isFocusOnCopyControls = false
      this.isShowTextToCopy = true
    },
    hideTextToCopy: function () {
      this.isShowTextToCopy = false
    },
    pasteContent: function (copiedAnswer) {
      this.answer.answer = copiedAnswer
      this.hideTextToCopy()
      this.updateAnswerOnServer()
    },
    hasCachedAnswers: function () {
      return this.getCachedAnswers().length > 0
    },
    getCachedAnswers: function () {
      const values = this.answerCache[this.answer.question]
      if (values === undefined) {
        return []
      }
      // filter out duplicates
      const answers = Object.values(values).filter((ans, index, self) => {
        return self.indexOf(ans) === index
      })
      return answers
    },
    updateAnswerCache: function () {
      let values = this.answerCache[this.answer.question]
      if (values === undefined) {
        values = {}
        this.answerCache[this.answer.question] = values
      }
      values[this.answer.reference] = this.answer.answer
    },
    resizeCanvas: function () {
      const canvas = this.$refs.canvas
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = canvas.offsetWidth * ratio
      canvas.height = canvas.offsetHeight * ratio
      canvas.getContext('2d').scale(ratio, ratio)
    },
    hideSignaturePlaceholder: function () {
      this.showSignaturePlaceholder = false
    },
    clearSignature: function () {
      if (this.signaturePad) {
        this.signaturePad.clear()
      }
      if (this.answer.answer) {
        this.answer.answer = ''
        this.clearServerValidationState()
        this.$nextTick(() => {
          this.resizeCanvas()
        })
      }
    },
    saveSignature: function () {
      if (this.signaturePad) {
        if (this.signaturePad.isEmpty()) {
          this.updateAnswer()
          this.answerFailed = true
        } else {
          const dataURL = this.signaturePad.toDataURL('image/png')
          this.answer.answer = dataURL
          this.updateAnswer()
        }
      }
    },
    clearServerValidationState: function () {
      this.answerFailed = false
      this.answerAccepted = false
    },
    updateAnswerOnServer: function () {
      this.isShowUpdatingServer = true
      this.clearServerValidationState()

      const config = {
        headers: {
          'If-Match': this.answer.version
        }
      }
      const body = {
        type: this.answer.type,
        reference: this.answer.reference
      }

      if (this.isCheckBoxes) {
        body.answers = this.answer.answers
      } else {
        body.answer = this.answer.answer
      }

      this.$http.put('/guest/v1/guests/' + this.guestReference +
        '/responses/' + this.responseReference +
        '/answers/' + this.answer.reference, body, config)
        .then(response => {
          this.$root.$emit('answer-updated', response.data)
          this.answerAccepted = true
        }).catch(error => {
          this.answerFailed = true
          if (error.response) {
            if (error.response.status === 500) {
              this.serverErrorMessage = 'Problem saving your answer'
            } else if (error.response.status === 412) {
              this.reloadAnswer()
            } else {
              this.serverErrorMessage = error.response.data.message
            }
          } else {
            this.serverErrorMessage = 'Problem saving your answer'
          }
        }).finally(() => {
          this.isShowUpdatingServer = false
        })
    },
    updateFileAnswerOnServer: function () {
      this.isShowUpdatingServer = true
      this.clearServerValidationState()

      const config = {
        headers: {
          'If-Match': this.answer.version
        }
      }

      const formData = new FormData()
      formData.append('file', this.image, this.image.name)

      this.$http.put('/guest/v1/guests/' + this.guestReference +
        '/responses/' + this.responseReference +
        '/answers/' + this.answer.reference + '/file', formData, config)
        .then(response => {
          this.$root.$emit('answer-updated', response.data)
          this.answerAccepted = true
          // this.resetImage()
        }).catch(error => {
          this.answerFailed = true
          if (error.response) {
            if (error.response.status === 500) {
              this.serverErrorMessage = 'Problem saving your answer'
            } else if (error.response.status === 412) {
              this.reloadAnswer()
            } else {
              this.serverErrorMessage = error.response.data.message
            }
          } else {
            this.serverErrorMessage = 'Problem saving your answer'
          }
          if (this.answer.answer) {
            this.serverErrorMessage += '. Reloading old image...'
            setTimeout(() => {
              this.resetImage()
            }, 1000)
            setTimeout(() => {
              this.$root.$emit('reload-answer', this.answer.reference)
            }, 2000)
          }
        }).finally(() => {
          this.isShowUpdatingServer = false
        })
    },
    reloadAnswer: function () {
      this.serverErrorMessage = 'The answer was updated by someone else, reloading...'
      setTimeout(() => {
        this.$root.$emit('reload-answer', this.answer.reference)
      }, 2000)
    },
    updateAnswer: function () {
      this.clearServerValidationState()
      if (this.isCheckBoxes) {
        this.$refs.answerValidator.validate(this.answer.answers).then(result => {
          if (result.valid) {
            this.updateAnswerOnServer()
          }
        })
      } else {
        this.$refs.answerValidator.validate(this.answer.answer).then(result => {
          if (result.valid) {
            if (this.answer.copyable) {
              this.updateAnswerCache()
            }
            this.updateAnswerOnServer()
          }
        })
      }
    },
    answerReloaded: function (reloadedAnswer) {
      if (this.answer.reference === reloadedAnswer.reference) {
        this.serverErrorMessage = 'This answer has been reloaded and is up to date'
        setTimeout(() => {
          this.clearServerValidationState()
          this.checkIfAnswerAccepted()
        }, 5000)
      }
    },
    checkIfAnswerAccepted: function () {
      if ((this.answer !== undefined && this.answer.answer !== '' && this.answer.answer !== null) || this.isMultipleAnswersAndIsAnswered) {
        this.answerAccepted = true
      }
    }
  },
  watch: {
    answer: {
      handler: function () {
        if (this.answer.copyable && this.$refs.resizeableTextarea) {
          this.$nextTick(() => {
            this.resizeTextArea()
          })
        }
      },
      deep: true
    },
    sectionVisible: {
      handler: function (visible) {
        if (visible && this.isSignature) {
          this.$nextTick(() => {
            this.resizeCanvas()
          })
        }
      }
    }
  },
  mounted: function () {
    if (this.isSignature) {
      this.$nextTick(() => {
        const canvas = this.$refs.canvas
        this.resizeCanvas()
        this.signaturePad = new SignaturePad(canvas, {
          backgroundColor: 'white',
          penColor: 'black'
        })
        canvas.addEventListener('pointerdown', () => {
          this.hideSignaturePlaceholder()
        })
      })
      window.addEventListener('resize', this.resizeCanvas)
    }
    this.$nextTick(() => {
      if (this.answer.copyable && this.$refs.resizeableTextarea) {
        this.resizeTextArea()
      }
      this.checkIfAnswerAccepted()
    })
    this.$root.$on('answer-reloaded', this.answerReloaded)
  },
  beforeDestroy: function () {
    if (this.isSignature) {
      window.removeEventListener('resize', this.resizeCanvas)
    }
    this.$root.$off('answer-reloaded', this.answerReloaded)
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/components/_question_answer.scss";
</style>

<!--These are overrides for the flatpickr inputs -->
<style lang="scss">
.form-control {
  background-color: #fff !important;
  border-color: inherit;
  display: inline-block;
  width: inherit;
  margin-bottom: 5px;
}

.flatpickr-calendar.arrowTop:after {
  border-bottom-color: $blue;
}

.flatpickr-calendar.arrowBottom:after {
  border-top-color: $blue;
}

.flatpickr-months .flatpickr-month,
.flatpickr-current-month .flatpickr-monthDropdown-months,
.flatpickr-current-month .flatpickr-monthDropdown-months .flatpickr-monthDropdown-month,
.flatpickr-weekdays,
span.flatpickr-weekday {
  background-color: $blue;
  color: $white;
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange,
.flatpickr-day.selected.inRange,
.flatpickr-day.startRange.inRange,
.flatpickr-day.endRange.inRange,
.flatpickr-day.selected:focus,
.flatpickr-day.startRange:focus,
.flatpickr-day.endRange:focus,
.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover,
.flatpickr-day.selected.prevMonthDay,
.flatpickr-day.startRange.prevMonthDay,
.flatpickr-day.endRange.prevMonthDay,
.flatpickr-day.selected.nextMonthDay,
.flatpickr-day.startRange.nextMonthDay,
.flatpickr-day.endRange.nextMonthDay {
  background: $blue;
  border-color: $blue;
}

.flatpickr-day.selected.startRange+.endRange:not(:nth-child(7n+1)),
.flatpickr-day.startRange.startRange+.endRange:not(:nth-child(7n+1)),
.flatpickr-day.endRange.startRange+.endRange:not(:nth-child(7n+1)) {
  -webkit-box-shadow: -10px 0 0 $blue;
  box-shadow: -10px 0 0 $blue;
}

.flatpickr-day.week.selected {
  -webkit-box-shadow: -5px 0 0 $blue, 5px 0 0 $blue;
  box-shadow: -5px 0 0 $blue, 5px 0 0 $blue;
}

.vdpInnerWrap {
  min-width: 30em;
}

.vdpHeader {
  background-color: $blue;
  margin: -1em -1em -3.5em;
}

.vdpPeriodControl button {
  color: $white;
}

.vdpHeadCellContent {
  color: $white;
}

.vdpArrowPrev:after {
  border-right-color: $white;
}

.vdpArrowNext:after {
  border-left-color: $white;
}

.vdpCell.selectable:hover .vdpCellContent,
.vdpCell.selected .vdpCellContent {
  background: $blue;
}

.vdpWithInput>input {
  font-size: 1rem;
  padding-left: 10px;
}
</style>
